/* The Financial Architect. Hero 3D engine.
   A small painter-algorithm renderer written on a 2D canvas. No libraries,
   no WebGL context loss, no shader compile cost, and it degrades to a still
   image whenever motion is not welcome. */
(function () {
  'use strict';

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function num(el, key, fallback) {
    var v = parseFloat(el.getAttribute('data-' + key));
    return isNaN(v) ? fallback : v;
  }

  function Scene(mount) {
    this.mount = mount;
    this.canvas = mount.querySelector('.hero__canvas');
    this.fallback = mount.querySelector('.hero__fallback');
    this.ctx = this.canvas ? this.canvas.getContext('2d') : null;

    this.speed = num(mount, 'speed', 0.6);
    this.mouseStrength = num(mount, 'mouse', 0.5);
    this.tilt = num(mount, 'tilt', 16);
    this.light = num(mount, 'light', 1);
    this.glow = num(mount, 'glow', 0.8);
    this.distance = num(mount, 'distance', 9.5);
    this.mobileMode = mount.getAttribute('data-mobile') || 'simplified';

    this.accent = mount.getAttribute('data-accent') || '#2457FF';
    this.gold = mount.getAttribute('data-gold') || '#C6A15B';
    this.cyan = mount.getAttribute('data-cyan') || '#45C8D8';
    this.ink = mount.getAttribute('data-ink') || '#F8F7F3';

    this.rotY = -0.5;
    this.tx = 0; this.ty = 0;
    this.mx = 0; this.my = 0;
    this.t = 0;
    this.visible = true;
    this.running = false;
    this.simple = false;

    this.build();
  }

  Scene.prototype.rgba = function (hex, a) {
    var h = hex.replace('#', '');
    if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
    var n = parseInt(h, 16);
    return 'rgba(' + ((n >> 16) & 255) + ',' + ((n >> 8) & 255) + ',' + (n & 255) + ',' + a + ')';
  };

  /* ------------------------------------------------------------- geometry */
  Scene.prototype.build = function () {
    var S = this;
    var solids = [];
    var wires = [];

    function plate(cx, cy, cz, w, d, color, alpha, stroke) {
      solids.push({
        pts: [
          [cx - w / 2, cy, cz - d / 2],
          [cx + w / 2, cy, cz - d / 2],
          [cx + w / 2, cy, cz + d / 2],
          [cx - w / 2, cy, cz + d / 2]
        ],
        fill: color, alpha: alpha, stroke: stroke, float: 0
      });
    }

    /* central illuminated foundation */
    plate(0, -1.6, 0, 5.4, 5.4, S.accent, 0.1, S.rgba(S.accent, 0.55));
    plate(0, -1.55, 0, 3.2, 3.2, S.cyan, 0.14, S.rgba(S.cyan, 0.75));

    /* floating glass platforms, stepped like a structural model */
    var decks = [
      [-1.5, 0.25, -0.6, 3.0, 2.2, S.accent, 0.13],
      [1.7, 1.15, 0.7, 2.4, 1.8, S.cyan, 0.1],
      [-0.4, 2.1, 1.4, 1.9, 1.4, S.gold, 0.1],
      [2.3, -0.55, -1.6, 1.6, 1.6, S.accent, 0.09]
    ];
    decks.forEach(function (d, i) {
      solids.push({
        pts: [
          [d[0] - d[3] / 2, d[1], d[2] - d[4] / 2],
          [d[0] + d[3] / 2, d[1], d[2] - d[4] / 2],
          [d[0] + d[3] / 2, d[1], d[2] + d[4] / 2],
          [d[0] - d[3] / 2, d[1], d[2] + d[4] / 2]
        ],
        fill: d[5], alpha: d[6], stroke: S.rgba(d[5], 0.6), float: 0.09 + i * 0.03, phase: i * 1.3
      });
    });

    /* architectural columns under the main decks */
    var cols = [[-2.6, -1.6, -1.4, 1.85], [-0.5, -1.6, 0.3, 1.85], [1.1, -1.6, -1.5, 2.75], [2.5, -1.6, 1.5, 2.7]];
    cols.forEach(function (c) {
      var s = 0.16;
      var x = c[0], y = c[1], z = c[2], h = c[3];
      var b = [[x - s, y, z - s], [x + s, y, z - s], [x + s, y, z + s], [x - s, y, z + s]];
      var t = b.map(function (p) { return [p[0], y + h, p[2]]; });
      for (var i = 0; i < 4; i++) {
        var j = (i + 1) % 4;
        solids.push({ pts: [b[i], b[j], t[j], t[i]], fill: S.ink, alpha: 0.05, stroke: S.rgba(S.ink, 0.22), float: 0 });
      }
      solids.push({ pts: t, fill: S.gold, alpha: 0.16, stroke: S.rgba(S.gold, 0.5), float: 0 });
    });

    /* the bridge: a segmented span connecting two decks */
    var span = [];
    for (var i = 0; i <= 10; i++) {
      var u = i / 10;
      var x = -2.4 + u * 5.0;
      var y = 0.55 + Math.sin(u * Math.PI) * 0.9;
      var z = -0.9 + u * 1.8;
      span.push([x, y, z]);
    }
    for (var k = 0; k < span.length - 1; k++) {
      var a = span[k], b2 = span[k + 1];
      solids.push({
        pts: [[a[0], a[1], a[2] - 0.28], [b2[0], b2[1], b2[2] - 0.28], [b2[0], b2[1], b2[2] + 0.28], [a[0], a[1], a[2] + 0.28]],
        fill: S.cyan, alpha: 0.12, stroke: S.rgba(S.cyan, 0.45), float: 0
      });
      wires.push({ pts: [a, [a[0], a[1] + 0.75, a[2]]], color: S.rgba(S.gold, 0.35), width: 1 });
    }
    wires.push({ pts: span.map(function (p) { return [p[0], p[1] + 0.78, p[2]]; }), color: S.rgba(S.gold, 0.55), width: 1.2 });

    /* metallic document cards, held upright like drawings on a rack */
    var cards = [[-2.2, 1.35, 1.2, -0.4], [-1.4, 1.75, 0.6, -0.2], [2.6, 0.6, -0.4, 0.35]];
    cards.forEach(function (c, i) {
      var w = 0.95, h = 1.25, lean = c[3];
      var x = c[0], y = c[1], z = c[2];
      solids.push({
        pts: [
          [x - w / 2, y, z],
          [x + w / 2, y, z],
          [x + w / 2 + lean * 0.2, y + h, z + lean],
          [x - w / 2 + lean * 0.2, y + h, z + lean]
        ],
        fill: S.ink, alpha: 0.09, stroke: S.rgba(S.ink, 0.4), float: 0.12, phase: i * 2.1
      });
      for (var r = 1; r <= 3; r++) {
        var ry = y + (h / 4) * r;
        var rz = z + lean * (r / 4);
        wires.push({
          pts: [[x - w / 2 + 0.14, ry, rz], [x + w / 2 - 0.2, ry, rz]],
          color: S.rgba(S.cyan, 0.4), width: 1, float: 0.12, phase: i * 2.1
        });
      }
    });

    /* coin-like discs, no symbols, purely structural tokens */
    var discs = [[-2.9, 1.9, -0.9, 0.42], [0.9, 2.6, -0.2, 0.34], [2.9, 1.9, 1.1, 0.3]];
    discs.forEach(function (d, i) {
      var pts = [];
      for (var a2 = 0; a2 < 18; a2++) {
        var ang = (a2 / 18) * Math.PI * 2;
        pts.push([d[0] + Math.cos(ang) * d[3], d[1] + Math.sin(ang) * d[3] * 0.22, d[2] + Math.sin(ang) * d[3]]);
      }
      solids.push({ pts: pts, fill: S.gold, alpha: 0.22, stroke: S.rgba(S.gold, 0.7), float: 0.16, phase: i * 1.7, spin: true });
    });

    /* transparent blueprint layer above the scene */
    for (var L = 0; L < 3; L++) {
      var yy = 3.1 + L * 0.32;
      wires.push({
        pts: [[-3.2, yy, -2.2], [3.2, yy, -2.2], [3.2, yy, 2.2], [-3.2, yy, 2.2], [-3.2, yy, -2.2]],
        color: S.rgba(S.ink, 0.1 + L * 0.03), width: 1, float: 0.05, phase: L
      });
    }

    /* blueprint ground grid */
    for (var g = -4; g <= 4; g++) {
      wires.push({ pts: [[g, -1.62, -4], [g, -1.62, 4]], color: S.rgba(S.ink, 0.07), width: 1 });
      wires.push({ pts: [[-4, -1.62, g], [4, -1.62, g]], color: S.rgba(S.ink, 0.07), width: 1 });
    }

    /* the glowing directional line: the path a decision travels */
    this.path = [];
    for (var p = 0; p <= 60; p++) {
      var u2 = p / 60;
      this.path.push([
        -3.4 + u2 * 6.8,
        -1.45 + Math.sin(u2 * Math.PI) * 2.4,
        1.9 - u2 * 3.6
      ]);
    }

    this.solids = solids;
    this.wires = wires;
  };

  /* ------------------------------------------------------------ projection */
  Scene.prototype.project = function (p, out) {
    var cosY = this.cosY, sinY = this.sinY, cosX = this.cosX, sinX = this.sinX;
    var x = p[0] * cosY - p[2] * sinY;
    var z = p[0] * sinY + p[2] * cosY;
    var y = p[1] * cosX - z * sinX;
    var zz = p[1] * sinX + z * cosX;
    var d = zz + this.distance;
    if (d < 0.35) d = 0.35;
    var f = this.focal / d;
    out[0] = this.cx + x * f;
    out[1] = this.cy - y * f;
    out[2] = d;
    return out;
  };

  /* --------------------------------------------------------------- render */
  Scene.prototype.resize = function () {
    var r = this.mount.getBoundingClientRect();
    var cap = this.simple ? 1.5 : 2;
    var dpr = Math.min(window.devicePixelRatio || 1, cap);
    this.w = Math.max(r.width, 1);
    this.h = Math.max(r.height, 1);
    this.canvas.width = Math.round(this.w * dpr);
    this.canvas.height = Math.round(this.h * dpr);
    this.canvas.style.width = this.w + 'px';
    this.canvas.style.height = this.h + 'px';
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.cx = this.w * (this.w > 900 ? 0.68 : 0.5);
    this.cy = this.h * 0.56;
    this.focal = Math.min(this.w, this.h) * 0.85;
  };

  Scene.prototype.draw = function () {
    var ctx = this.ctx;
    var S = this;
    ctx.clearRect(0, 0, this.w, this.h);

    var ry = this.rotY;
    var rx = (this.tilt * Math.PI) / 180 + this.ty * 0.22 * this.mouseStrength;
    ry += this.tx * 0.55 * this.mouseStrength;
    this.cosY = Math.cos(ry); this.sinY = Math.sin(ry);
    this.cosX = Math.cos(rx); this.sinX = Math.sin(rx);

    var tmp = [0, 0, 0];
    var queue = [];

    this.solids.forEach(function (s) {
      var lift = s.float ? Math.sin(S.t * 0.55 + (s.phase || 0)) * s.float : 0;
      var spin = s.spin ? S.t * 0.4 : 0;
      var proj = [];
      var depth = 0;
      for (var i = 0; i < s.pts.length; i++) {
        var p = s.pts[i];
        var px = p[0], pz = p[2];
        if (spin) {
          var c = Math.cos(spin), sn = Math.sin(spin);
          px = p[0] * c - p[2] * sn;
          pz = p[0] * sn + p[2] * c;
        }
        S.project([px, p[1] + lift, pz], tmp);
        proj.push([tmp[0], tmp[1]]);
        depth += tmp[2];
      }
      queue.push({ kind: 'solid', z: depth / s.pts.length, proj: proj, s: s });
    });

    this.wires.forEach(function (w) {
      var lift = w.float ? Math.sin(S.t * 0.55 + (w.phase || 0)) * w.float : 0;
      var proj = [];
      var depth = 0;
      for (var i = 0; i < w.pts.length; i++) {
        var p = w.pts[i];
        S.project([p[0], p[1] + lift, p[2]], tmp);
        proj.push([tmp[0], tmp[1]]);
        depth += tmp[2];
      }
      queue.push({ kind: 'wire', z: depth / w.pts.length, proj: proj, s: w });
    });

    queue.sort(function (a, b) { return b.z - a.z; });

    for (var q = 0; q < queue.length; q++) {
      var item = queue[q];
      var proj = item.proj;
      ctx.beginPath();
      ctx.moveTo(proj[0][0], proj[0][1]);
      for (var i = 1; i < proj.length; i++) ctx.lineTo(proj[i][0], proj[i][1]);

      if (item.kind === 'solid') {
        ctx.closePath();
        var fade = Math.max(0.25, Math.min(1.35, 12 / item.z));
        ctx.fillStyle = this.rgba(item.s.fill, Math.min(0.75, item.s.alpha * fade * this.light));
        ctx.fill();
        ctx.lineWidth = 1;
        ctx.strokeStyle = item.s.stroke;
        ctx.stroke();
      } else {
        ctx.lineWidth = item.s.width || 1;
        ctx.strokeStyle = item.s.color;
        ctx.stroke();
      }
    }

    /* directional line with a travelling pulse */
    var pathProj = [];
    for (var pi = 0; pi < this.path.length; pi++) {
      this.project(this.path[pi], tmp);
      pathProj.push([tmp[0], tmp[1]]);
    }
    ctx.beginPath();
    ctx.moveTo(pathProj[0][0], pathProj[0][1]);
    for (var pj = 1; pj < pathProj.length; pj++) ctx.lineTo(pathProj[pj][0], pathProj[pj][1]);
    ctx.lineWidth = 1.2;
    ctx.strokeStyle = this.rgba(this.cyan, 0.28 * this.light);
    ctx.stroke();

    if (!this.simple) {
      var head = Math.floor(((this.t * 0.13) % 1) * (pathProj.length - 1));
      var tail = Math.max(0, head - 10);
      ctx.beginPath();
      ctx.moveTo(pathProj[tail][0], pathProj[tail][1]);
      for (var ph = tail + 1; ph <= head; ph++) ctx.lineTo(pathProj[ph][0], pathProj[ph][1]);
      ctx.lineWidth = 2;
      ctx.strokeStyle = this.rgba(this.cyan, 0.9);
      ctx.shadowBlur = 22 * this.glow;
      ctx.shadowColor = this.rgba(this.cyan, 0.9);
      ctx.stroke();

      /* accent glow on the illuminated foundation */
      this.project([0, -1.5, 0], tmp);
      var grd = ctx.createRadialGradient(tmp[0], tmp[1], 0, tmp[0], tmp[1], this.focal * 0.32);
      grd.addColorStop(0, this.rgba(this.accent, 0.32 * this.glow));
      grd.addColorStop(1, this.rgba(this.accent, 0));
      ctx.shadowBlur = 0;
      ctx.fillStyle = grd;
      ctx.beginPath();
      ctx.arc(tmp[0], tmp[1], this.focal * 0.32, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.shadowBlur = 0;
  };

  Scene.prototype.frame = function (now) {
    if (!this.running) return;
    var dt = Math.min((now - (this.last || now)) / 1000, 0.05);
    this.last = now;
    this.t += dt;
    this.rotY += dt * 0.12 * this.speed;
    this.tx += (this.mx - this.tx) * 0.05;
    this.ty += (this.my - this.ty) * 0.05;
    this.draw();
    this.raf = requestAnimationFrame(this.frame.bind(this));
  };

  Scene.prototype.start = function () {
    if (this.running) return;
    this.running = true;
    this.last = 0;
    this.raf = requestAnimationFrame(this.frame.bind(this));
  };

  Scene.prototype.stop = function () {
    this.running = false;
    if (this.raf) cancelAnimationFrame(this.raf);
  };

  Scene.prototype.showFallback = function () {
    if (this.canvas) this.canvas.style.display = 'none';
    if (this.fallback) this.fallback.style.display = 'block';
  };

  Scene.prototype.init = function () {
    var S = this;
    if (!this.ctx) { this.showFallback(); return; }

    var narrow = window.innerWidth < 750;
    if (narrow && this.mobileMode === 'static') { this.showFallback(); return; }
    if (narrow && this.mobileMode === 'simplified') this.simple = true;
    if ((navigator.hardwareConcurrency || 4) <= 2) this.simple = true;

    if (this.fallback) this.fallback.style.display = 'none';
    this.resize();

    if (reduced) {
      this.draw();
      return;
    }

    if (!narrow || this.mobileMode !== 'off') {
      window.addEventListener('pointermove', function (e) {
        if (window.innerWidth < 750) return;
        S.mx = (e.clientX / window.innerWidth - 0.5) * 2;
        S.my = (e.clientY / window.innerHeight - 0.5) * 2;
      }, { passive: true });
    }

    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () { S.resize(); S.draw(); }, 160);
    });

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) S.stop();
      else if (S.visible) S.start();
    });

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          S.visible = entry.isIntersecting;
          if (entry.isIntersecting && !document.hidden) S.start();
          else S.stop();
        });
      }, { threshold: 0.02 }).observe(this.mount);
    } else {
      this.start();
    }
  };

  function boot() {
    document.querySelectorAll('[data-hero3d]').forEach(function (mount) {
      if (mount.__scene) mount.__scene.stop();
      var scene = new Scene(mount);
      mount.__scene = scene;
      scene.init();
    });
  }

  if (document.readyState !== 'loading') boot();
  else document.addEventListener('DOMContentLoaded', boot);

  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('shopify:section:unload', function (e) {
    var mount = e.target.querySelector('[data-hero3d]');
    if (mount && mount.__scene) mount.__scene.stop();
  });
})();
